import React, { useMemo } from "react";
import { Tag, Tooltip, Button, theme } from "antd";
import { BarChartOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useResizableColumns } from "../../Common/Custom Components/Ant design/useResizableColumns";

export interface PracticeManagementRecord {
  id: number;
  sno?: number;
  name:
    | string
    | { title?: string; first?: string; last?: string; alias?: string };
  phoneNumber?: string;
  email?: string;
  inActive?: boolean;
  practices?: Array<{ id: number; name: string }>;
  creditNoteReviewAmount?: number;
}

export interface UsePracticeManagementColumnsOptions {
  currentPage: number;
  pageSize: number;
  currentSort: { sortCol: string; sortDir: "ascend" | "descend" };
  onOpenQuickDrawer: (record: PracticeManagementRecord) => void;
  onCopyId: (id: number) => void;
}

export const formatUserName = (user: any): string => {
  if (!user) return "";
  if (typeof user === "string") return user;
  const nameObj = user.name || user;
  if (typeof nameObj === "string") return nameObj;
  if (typeof nameObj === "object" && nameObj !== null) {
    const parts = [nameObj.first, nameObj.last].filter(Boolean);
    if (parts.length > 0) return parts.join(" ");
    if (nameObj.alias) return nameObj.alias;
    if (nameObj.title) return nameObj.title;
  }
  return "";
};

export const usePracticeManagementColumns = ({
  currentPage,
  pageSize,
  currentSort,
  onOpenQuickDrawer,
  onCopyId,
}: UsePracticeManagementColumnsOptions) => {
  const { token } = theme.useToken();

  const rawColumns = useMemo(() => {
    return [
      {
        title: "S.No",
        key: "sno",
        width: 60,
        toggleable: false,
        align: "center" as const,
        render: (_: any, record: PracticeManagementRecord, index: number) => (
          <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>
            {record.sno ?? (currentPage - 1) * pageSize + index + 1}
          </span>
        ),
      },
      {
        title: "Tenant ID",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: "id",
        key: "id",
        width: 110,
        align: "center" as const,
        sortOrder:
          currentSort.sortCol === "id" || currentSort.sortCol === "_id"
            ? currentSort.sortDir
            : undefined,
        toggleable: false,
        render: (id: number) => (
          <Tooltip title="Click to copy Tenant ID">
            <Tag
              bordered={false}
              color="blue"
              onClick={() => onCopyId(id)}
              style={{
                margin: 0,
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 4,
                padding: "2px 8px",
                cursor: "pointer",
                display: "inline-block",
              }}
            >
              #{id}
            </Tag>
          </Tooltip>
        ),
      },
      {
        title: "Practice Name",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: "name",
        key: "name",
        width: 280,
        sortOrder:
          currentSort.sortCol === "Name" || currentSort.sortCol === "name"
            ? currentSort.sortDir
            : undefined,
        toggleable: false,
        render: (text: any, record: PracticeManagementRecord) => {
          const displayName =
            formatUserName(text) || formatUserName(record.name) || "—";
          return (
            <Link
              to={`/practices/${record.id}`}
              state={{ practiceName: displayName }}
              style={{
                fontWeight: 600,
                color: token.colorPrimary,
                display: "inline-block",
                maxWidth: 260,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "middle",
              }}
              title={displayName}
            >
              {displayName}
            </Link>
          );
        },
      },
      {
        title: "Email",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: "email",
        key: "email",
        width: 240,
        sortOrder:
          currentSort.sortCol === "Email" || currentSort.sortCol === "email"
            ? currentSort.sortDir
            : undefined,
        render: (email: string) =>
          email ? (
            <a
              href={`mailto:${email}`}
              style={{
                color: token.colorTextSecondary,
                fontSize: 13,
                display: "inline-block",
                maxWidth: 220,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={email}
            >
              {email}
            </a>
          ) : (
            <span style={{ color: token.colorTextQuaternary }}>—</span>
          ),
      },
      {
        title: "Phone Number",
        sorter: true,
        showSorterTooltip: false,
        dataIndex: "phoneNumber",
        key: "phoneNumber",
        width: 170,
        sortOrder:
          currentSort.sortCol === "PhoneNumber" ||
          currentSort.sortCol === "phoneNumber"
            ? currentSort.sortDir
            : undefined,
        render: (phone: string) =>
          phone ? (
            <a
              href={`tel:${phone}`}
              style={{ color: token.colorTextSecondary, fontSize: 13 }}
            >
              {phone}
            </a>
          ) : (
            <span style={{ color: token.colorTextQuaternary }}>—</span>
          ),
      },
      {
        title: "Status",
        dataIndex: "inActive",
        key: "inActive",
        width: 90,
        align: "center" as const,
        render: (inActive: boolean) => (
          <Tag
            color={inActive ? "default" : "success"}
            bordered={false}
            style={{
              fontWeight: 500,
              fontSize: 11,
              borderRadius: 4,
              padding: "1px 8px",
            }}
          >
            {inActive ? "Inactive" : "Active"}
          </Tag>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 70,
        fixed: "right" as const,
        align: "center" as const,
        toggleable: false,
        render: (_: any, record: PracticeManagementRecord) => (
          <Tooltip title="Quick Analytics Summary">
            <Button
              type="text"
              size="small"
              icon={
                <BarChartOutlined
                  style={{ fontSize: 15, color: token.colorPrimary }}
                />
              }
              onClick={() => onOpenQuickDrawer(record)}
            />
          </Tooltip>
        ),
      },
    ];
  }, [currentPage, pageSize, currentSort, token, onOpenQuickDrawer, onCopyId]);

  const { columns, components, columnSelector } = useResizableColumns({
    columns: rawColumns,
    storageKey: "ao_practices_management_columns_clean",
  });

  return {
    columns,
    components,
    columnSelector,
  };
};

export default usePracticeManagementColumns;
